/**
 * Thin wrapper around the /canvass/* lifecycle endpoints shipped in
 * `backend/app/routers/canvass.py`.
 *
 * Unlike `src/api/analytics.ts` these calls are ALWAYS live — the canvass
 * lifecycle endpoints exercise real DB state (creating shifts, writing
 * knockbacks, persisting AARs) so there is no meaningful mock mode. If
 * the backend is unreachable, the caller sees a network error and
 * renders a red state — never fall back to an in-memory fixture here.
 *
 * Every function returns the shape declared in `@/types/canvass.ts`,
 * which mirrors `backend/app/schemas/canvass.py` one-for-one.
 *
 * Role walls: the backend enforces them. The wrapper doesn't.
 *   - CANVASSER+ can hit anything under /shifts, /knocks, /aar, /issues
 *     GET / self-submit paths
 *   - COORDINATOR+ can review AARs (PATCH /aars/{id}/review)
 *   - ORG_MANAGER+ can CRUD campaign issues
 * A 403 from the live call is therefore not a frontend bug — surface it
 * to the user as "Your role does not permit this action."
 */
import client from '@/api/client';
import {
  enqueueKnock,
  synthesizeOptimisticKnock,
} from '@/lib/canvassOfflineQueue';
import type {
  AARCreate,
  AARPrefill,
  AARRead,
  AARReviewRequest,
  CampaignIssueCreate,
  CampaignIssueRead,
  CampaignIssueUpdate,
  CampaignTalkingPointCreate,
  CampaignTalkingPointRead,
  CampaignTalkingPointUpdate,
  CanvassShiftCreate,
  CanvassShiftRead,
  CanvassShiftStatus,
  CanvassShiftUpdate,
  KnockCreate,
  KnockRead,
  KnockResponseRead,
  KnockResponseUpdate,
  ShiftAbandonRequest,
  ShiftBriefing,
  FieldDeviceBeaconPing,
  FieldDeviceBeaconRead,
  FieldDevicesSummary,
  RevisitQueueResponse,
} from '@/types/canvass';

// ---------------------------------------------------------------------------
// Shifts
// ---------------------------------------------------------------------------

export interface ListShiftsOptions {
  status?: CanvassShiftStatus;
  limit?: number;
}

export async function listShifts(
  opts: ListShiftsOptions = {},
): Promise<CanvassShiftRead[]> {
  const params: Record<string, string | number> = {};
  if (opts.status) params.status = opts.status;
  if (opts.limit !== undefined) params.limit = opts.limit;
  const res = await client.get<CanvassShiftRead[]>('/canvass/shifts', {
    params,
  });
  return res.data;
}

export async function createShift(
  body: CanvassShiftCreate,
): Promise<CanvassShiftRead> {
  const res = await client.post<CanvassShiftRead>('/canvass/shifts', body);
  return res.data;
}

export async function getShift(shiftId: number): Promise<CanvassShiftRead> {
  const res = await client.get<CanvassShiftRead>(`/canvass/shifts/${shiftId}`);
  return res.data;
}

export async function updateShift(
  shiftId: number,
  body: CanvassShiftUpdate,
): Promise<CanvassShiftRead> {
  const res = await client.patch<CanvassShiftRead>(
    `/canvass/shifts/${shiftId}`,
    body,
  );
  return res.data;
}

export async function startShift(shiftId: number): Promise<CanvassShiftRead> {
  const res = await client.patch<CanvassShiftRead>(
    `/canvass/shifts/${shiftId}/start`,
  );
  return res.data;
}

export async function endShift(shiftId: number): Promise<CanvassShiftRead> {
  const res = await client.patch<CanvassShiftRead>(
    `/canvass/shifts/${shiftId}/end`,
  );
  return res.data;
}

export async function abandonShift(
  shiftId: number,
  body: ShiftAbandonRequest,
): Promise<CanvassShiftRead> {
  const res = await client.patch<CanvassShiftRead>(
    `/canvass/shifts/${shiftId}/abandon`,
    body,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Stage 1 — Briefing
// ---------------------------------------------------------------------------

export async function getBriefing(shiftId: number): Promise<ShiftBriefing> {
  const res = await client.get<ShiftBriefing>(
    `/canvass/shifts/${shiftId}/briefing`,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Stage 3 — Knocks + responses
// ---------------------------------------------------------------------------

export async function createKnock(
  shiftId: number,
  body: KnockCreate,
): Promise<KnockRead> {
  const res = await client.post<KnockRead>(
    `/canvass/shifts/${shiftId}/knocks`,
    body,
  );
  return res.data;
}

/**
 * Resilient variant of createKnock — try the network, and if (and only if)
 * the network itself is unreachable, persist the payload to the IndexedDB
 * offline queue and return a synthetic optimistic KnockRead so the UI can
 * immediately stripe the stop as "knocked".
 *
 * Important distinction:
 *   - NO response at all (request never made it) → queue it.
 *   - 4xx / 5xx response (backend said no for a real reason — validation,
 *     role wall, not-in-progress shift) → re-throw. Queueing a 422 is
 *     useless; it will just 422 again when we replay, and the user needs
 *     to see the actual error now.
 *
 * The "is this a network error" heuristic is "axios error with no
 * response object". Axios sets `error.response` iff the server replied
 * with a status line, so `!error.response` is the reliable signal for
 * "didn't reach the server" across fetch / XHR / timeout / CORS preflight
 * failure.
 */
export async function createKnockResilient(
  shiftId: number,
  body: KnockCreate,
): Promise<KnockRead> {
  try {
    return await createKnock(shiftId, body);
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const e = err as any;
    const hasResponse = Boolean(e?.response);
    if (hasResponse) {
      // Server replied — this is a real failure, not an offline blip.
      throw err;
    }
    // Network unreachable. Queue and return an optimistic synthetic knock.
    const queued = await enqueueKnock(shiftId, body);
    return synthesizeOptimisticKnock(queued);
  }
}

export async function listShiftKnocks(shiftId: number): Promise<KnockRead[]> {
  const res = await client.get<KnockRead[]>(
    `/canvass/shifts/${shiftId}/knocks`,
  );
  return res.data;
}

export async function updateKnockResponse(
  knockbackId: number,
  body: KnockResponseUpdate,
): Promise<KnockResponseRead> {
  const res = await client.patch<KnockResponseRead>(
    `/canvass/knocks/${knockbackId}/response`,
    body,
  );
  return res.data;
}

/**
 * Upload a door photo for a knock.  Sends multipart/form-data; the
 * backend EXIF-strips the image, saves to disk, and returns the updated
 * KnockResponse with `photo_path` populated.
 */
export async function uploadKnockPhoto(
  knockbackId: number,
  file: File,
): Promise<KnockResponseRead> {
  const form = new FormData();
  form.append('file', file);
  const res = await client.post<KnockResponseRead>(
    `/canvass/knocks/${knockbackId}/photo`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

/**
 * Build a full URL for a photo_path returned by the backend.
 * The backend serves photos at /uploads/photos/{tenant_id}/{filename}.
 */
export function photoUrl(photoPath: string): string {
  // `client.defaults.baseURL` is typically '/api' or similar.
  // Photos are served from the root of the backend, not under /api.
  const base = (client.defaults.baseURL || '').replace(/\/api\/?$/, '');
  return `${base}/uploads/photos/${photoPath}`;
}

// ---------------------------------------------------------------------------
// Stage 4 — AAR
// ---------------------------------------------------------------------------

export async function getAARPrefill(shiftId: number): Promise<AARPrefill> {
  const res = await client.get<AARPrefill>(
    `/canvass/shifts/${shiftId}/aar`,
  );
  return res.data;
}

export async function submitAAR(
  shiftId: number,
  body: AARCreate,
): Promise<AARRead> {
  const res = await client.post<AARRead>(
    `/canvass/shifts/${shiftId}/aar`,
    body,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Stage 5 — Coordinator review
// ---------------------------------------------------------------------------

export async function reviewAAR(
  aarId: number,
  body: AARReviewRequest,
): Promise<AARRead> {
  const res = await client.patch<AARRead>(
    `/canvass/aars/${aarId}/review`,
    body,
  );
  return res.data;
}

export interface ListAARsOptions {
  pending_only?: boolean;
  limit?: number;
}

export async function listAARs(
  opts: ListAARsOptions = {},
): Promise<AARRead[]> {
  const params: Record<string, string | number | boolean> = {};
  if (opts.pending_only !== undefined) params.pending_only = opts.pending_only;
  if (opts.limit !== undefined) params.limit = opts.limit;
  const res = await client.get<AARRead[]>('/canvass/aars', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Campaign issues (Stage 3 picker source)
// ---------------------------------------------------------------------------

export async function listIssues(
  includeInactive = false,
): Promise<CampaignIssueRead[]> {
  const res = await client.get<CampaignIssueRead[]>('/canvass/issues', {
    params: includeInactive ? { include_inactive: true } : {},
  });
  return res.data;
}

export async function createIssue(
  body: CampaignIssueCreate,
): Promise<CampaignIssueRead> {
  const res = await client.post<CampaignIssueRead>('/canvass/issues', body);
  return res.data;
}

export async function updateIssue(
  issueId: number,
  body: CampaignIssueUpdate,
): Promise<CampaignIssueRead> {
  const res = await client.patch<CampaignIssueRead>(
    `/canvass/issues/${issueId}`,
    body,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Campaign talking points (Stage 1 briefing copy)
// ---------------------------------------------------------------------------

export async function listTalkingPoints(
  includeInactive = false,
): Promise<CampaignTalkingPointRead[]> {
  const res = await client.get<CampaignTalkingPointRead[]>(
    '/canvass/talking-points',
    {
      params: includeInactive ? { include_inactive: true } : {},
    },
  );
  return res.data;
}

export async function createTalkingPoint(
  body: CampaignTalkingPointCreate,
): Promise<CampaignTalkingPointRead> {
  const res = await client.post<CampaignTalkingPointRead>(
    '/canvass/talking-points',
    body,
  );
  return res.data;
}

export async function updateTalkingPoint(
  tpId: number,
  body: CampaignTalkingPointUpdate,
): Promise<CampaignTalkingPointRead> {
  const res = await client.patch<CampaignTalkingPointRead>(
    `/canvass/talking-points/${tpId}`,
    body,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Field device beacons (coordinator queue observability)
// ---------------------------------------------------------------------------

/**
 * Canvasser heartbeat — fire-and-forget POST that upserts the current
 * IndexedDB queue count to the backend.  Silently swallows errors because
 * a beacon failure must never block the canvasser's workflow.
 */
export async function sendFieldDeviceBeacon(
  body: FieldDeviceBeaconPing,
): Promise<FieldDeviceBeaconRead | null> {
  try {
    const res = await client.post<FieldDeviceBeaconRead>(
      '/canvass/field-devices/beacon',
      body,
    );
    return res.data;
  } catch {
    // Fail silently — a dead beacon is never worse than no beacon.
    return null;
  }
}

/**
 * Coordinator view — fetch all field device beacons for the tenant.
 */
export async function listFieldDevices(
  staleMinutes = 5,
): Promise<FieldDevicesSummary> {
  const res = await client.get<FieldDevicesSummary>(
    '/canvass/field-devices',
    { params: { stale_minutes: staleMinutes } },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Revisit queue
// ---------------------------------------------------------------------------

export interface RevisitQueueOptions {
  /** Staleness window. Omit to take the server's default (21 days). */
  days?: number;
  limit?: number;
  routeId?: number;
}

/**
 * The doors worth going back to — come-backs first, then not-homes.
 *
 * CANVASSER+, and scoped server-side to the caller's own turf, so this is safe
 * to call unconditionally from the canvasser home screen. Params are omitted
 * when undefined rather than sent as nulls, so the server's defaults stay the
 * single source of truth for the window and the page size.
 */
export async function getRevisitQueue(
  opts: RevisitQueueOptions = {},
): Promise<RevisitQueueResponse> {
  const params: Record<string, number> = {};
  if (opts.days !== undefined) params.days = opts.days;
  if (opts.limit !== undefined) params.limit = opts.limit;
  if (opts.routeId !== undefined) params.route_id = opts.routeId;
  const res = await client.get<RevisitQueueResponse>(
    '/canvass/revisit-queue',
    { params },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Error helper — consistent mapping from axios error → user-facing message
// ---------------------------------------------------------------------------

/**
 * Turn an axios error into a short string a view can display.
 *
 * The backend uses HTTP status codes consistently for role + state issues:
 *   401 — not authenticated
 *   403 — authenticated but role-walled
 *   404 — not found (also hides cross-tenant reads — treat as not found)
 *   409 — lifecycle conflict (e.g. starting an already-in-progress shift)
 *   422 — validation error from Pydantic
 * Anything else is a backend bug or a network failure; fall back to a
 * generic "Something went wrong" rather than leaking the raw detail.
 */
export function formatCanvassError(err: unknown): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  const status = e?.response?.status as number | undefined;
  const detail = e?.response?.data?.detail as string | undefined;
  if (status === 401) {
    return 'Not authenticated. Log in and try again.';
  }
  if (status === 403) {
    return (
      'Your role does not permit this action.' +
      (detail ? ` (${detail})` : '')
    );
  }
  if (status === 404) {
    return detail || 'Not found.';
  }
  if (status === 409) {
    return detail || 'This operation is not valid in the current state.';
  }
  if (status === 422) {
    return detail || 'The form has invalid fields.';
  }
  if (!status) {
    return 'Could not reach the backend.';
  }
  return detail || `Backend error ${status}.`;
}
